import { Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback, useEffect } from 'react';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { QuotaSpaceForPromo } from 'reactApp/ui/OverquotaPopup/QuotaSpaceForPromo';

import styles from './OverquotaPopup.css';

export interface OverquotaPopupProps {
    isPrequota?: boolean;
    primaryButtonText?: string;
    secondaryButtonText?: string;

    onShow?(): void;

    onClick?(): void;

    onSecondaryClick?(): void;

    onDismiss?(): void;

    onClose?(): void;
}

const getTitle = (isPrequota = false) => {
    if (isPrequota) {
        return 'Скоро нельзя будет отправлять письма в Почте и загружать файлы в Облако';
    }

    if (IS_PHONE_BROWSER) {
        return 'Пока нельзя делиться файлами и загружать новые';
    }

    return (
        <>
            Пока нельзя делиться файлами
            <br />
            и&nbsp;загружать&nbsp;новые, но есть решение
        </>
    );
};

const getContent = (isPrequota = false) => {
    if (isPrequota) {
        return 'Единое пространство Почты и Облака почти заполнено — увеличьте его с подпиской Mail Space, чтобы не повторить недавние ограничения';
    }

    if (IS_PHONE_BROWSER) {
        return 'Закончилось место в Облаке и Почте — увеличьте его с подпиской Mail Space';
    }

    return 'Пространство Облака и Почты переполнено&nbsp;—&nbsp;увеличьте его с подпиской Mail Space, чтобы снять ограничения';
};

export const OverquotaPopup = memo(function OverquotaPopup({
    primaryButtonText = 'Снять ограничения',
    secondaryButtonText,
    isPrequota,
    onShow,
    onDismiss,
    onClick,
    onSecondaryClick,
    onClose,
}: OverquotaPopupProps): ReactElement | null {
    const id = isPrequota ? 'preoverquota-alert' : 'overquota-alert-clear';

    useEffect(() => {
        onShow?.();
    }, [onShow]);

    const onPrimaryHandler = useCallback(() => {
        onClick?.();
        onClose?.();
    }, []);

    const onSecondaryHandler = useCallback(() => {
        onSecondaryClick?.();
        onClose?.();
    }, []);

    const onCloseHandler = useCallback(() => {
        onDismiss?.();
        onClose?.();
    }, [onDismiss]);

    if (IS_PHONE_BROWSER) {
        return (
            <MobileDialog
                id="overquota-alert-clear-mobile"
                onClose={onCloseHandler}
                topmost
                mod="base"
                isRebrandingQuotaLanding
                closeMode="darkAndRound"
            >
                <div className={styles.rootMobile}>
                    <QuotaSpaceForPromo isPrequota={isPrequota} />
                    <Text className={styles.title}>{getTitle(isPrequota)}</Text>
                    <Text className={styles.text}>{getContent(isPrequota)}</Text>
                    <div className={styles.footerWrapper}>
                        <Button theme="vk" primary onClick={onPrimaryHandler} sizeMode={ButtonSizeMode.big}>
                            {primaryButtonText}
                        </Button>
                        {!!secondaryButtonText && (
                            <Button theme="vk" onClick={onSecondaryHandler} sizeMode={ButtonSizeMode.big}>
                                {secondaryButtonText}
                            </Button>
                        )}
                    </div>
                </div>
            </MobileDialog>
        );
    }

    return (
        <Dialog
            open
            header={''}
            closeOnDimmerClick={false}
            id={id}
            qaId={id}
            onCancel={onCloseHandler}
            className={classNames(styles.root, { [styles.preQuota]: isPrequota })}
            size="tiny"
        >
            <Content isModal>
                <div className={styles.content}>
                    <div className={styles.imageWrapper}>
                        <QuotaSpaceForPromo isPrequota={isPrequota} />
                    </div>
                    <div className={styles.title}>{getTitle(isPrequota)}</div>
                    <div className={styles.text}>{getContent(isPrequota)}</div>
                    <div className={styles.footerWrapper}>
                        <Button
                            className={styles.button}
                            data-name="freeze-renewal"
                            theme="octavius"
                            sizeMode={ButtonSizeMode.small}
                            onClick={onPrimaryHandler}
                            primary
                        >
                            {primaryButtonText}
                        </Button>
                        {!!secondaryButtonText && (
                            <Button
                                className={styles.button}
                                data-name="secondary-bttn"
                                theme="octavius"
                                sizeMode={ButtonSizeMode.small}
                                onClick={onSecondaryHandler}
                            >
                                {secondaryButtonText}
                            </Button>
                        )}
                    </div>
                </div>
            </Content>
        </Dialog>
    );
});

OverquotaPopup.displayName = 'OverquotaPopup';
